import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ViewsMenu } from '@/components/ViewsMenu';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { isMenuPage, isScopeAware } from '@/lib/navigation';

interface Props {
  title: string;
  /** "8 results" / count of items shown. */
  count?: number | null;
  isFetching?: boolean;
  /** Subtitle line, e.g. "Assigned to me · Excluding Complete · Updated 14 min ago". */
  subtitle?: React.ReactNode;
  /** Right-side action cluster (buttons, filters, etc.). */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Consistent page-title row used on every list page.
 *  ┌──────────────────────────────────────────────────────────────────┐
 *  │  My Tickets    8 results              [Views ▾] [Export] [New +]  │
 *  │  Assigned to me · Excluding Complete · 14 min ago                │
 *  └──────────────────────────────────────────────────────────────────┘
 */
export function PageHeader({ title, count, isFetching, subtitle, actions, className }: Props) {
  const { pathname } = useLocation();
  const { scope } = useClientScope();
  // A client is picked but this list page doesn't filter by it — say so rather than look filtered.
  const unscoped = !!scope && isMenuPage(pathname) && !isScopeAware(pathname);
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <h1 className="m-0 text-[22px] font-[650] tracking-[-0.018em] text-foreground">
            {title}
          </h1>
          {count != null && (
            <span className="hidden items-center gap-1.5 text-xs tabular-nums text-muted-foreground md:inline-flex">
              {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
              {count} result{count === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {subtitle && <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>}
        {unscoped && <div className="mt-1 text-xs text-muted-foreground/80">All clients: this page isn't filtered by the client picker.</div>}
      </div>
      {/* The page's pre-filtered views (lib/navigation.ts) sit left of its own actions/search. */}
      <div className="flex shrink-0 items-center gap-2 empty:hidden">
        <ViewsMenu />
        {actions}
      </div>
    </div>
  );
}
